// @ts-strict-ignore
import React from 'react'
import { TFunction } from 'react-i18next'
import {
  Avatar,
  Button,
  DataTableCellVariants,
  DataTableRowData,
  Highlight,
  Icon,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { CryptoPayout, MassPayoutCryptoBatch, RecordStatus } from 'pages/MassPayouts/types'
import { getCurrencyName } from 'pages/MassPayouts/utils'
import { Currency } from 'types/types'
import { formatErrorMsg } from 'utils/utils'

interface CryptoTableRowsProps {
  cryptoBatchPayout: MassPayoutCryptoBatch
  handleDeleteCryptoPayout: (payout: CryptoPayout) => void
  handleShowEditCryptoPayoutModal: (
    e: React.MouseEvent<HTMLButtonElement>,
    payout: CryptoPayout
  ) => void
  onOpenPayoutDetails: (payoutIndex?: number) => void
  t: TFunction<'translation', undefined>
  cryptoCurrencies: Currency[]
  darkMode?: boolean
}

export const getCryptoBatchTableData = ({
  cryptoBatchPayout,
  handleDeleteCryptoPayout,
  handleShowEditCryptoPayoutModal,
  onOpenPayoutDetails,
  t,
  cryptoCurrencies,
  darkMode,
}: CryptoTableRowsProps): DataTableRowData[] => {
  if (!cryptoBatchPayout?.items) return []

  return cryptoBatchPayout?.items?.map((payout, payoutIndex) => {
    return {
      id: payout?.externalId,
      testid: payout?.externalId,
      onRowClick: () => {
        onOpenPayoutDetails(payoutIndex)
      },
      cells: [
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: `${payout?.fundingWalletAmount?.toString() ?? payout?.paidCurrencyAmount?.toString()} ${
            payout?.fundingWalletAmount ? cryptoBatchPayout?.walletCurrency : payout?.currency
          }`,
        },
        {
          key: 'recipient',
          variant: DataTableCellVariants.TEXT,
          Avatar: (
            <Avatar
              name={getCurrencyName(
                payout?.currency,
                payout?.protocol || payout?.network,
                cryptoCurrencies
              )}
              darkMode={darkMode}
            />
          ),
          Text: ellipseMiddleText(payout?.address, 10, 10),
          BottomText: payout?.tag,
        },
        {
          key: 'paymentReference',
          variant: DataTableCellVariants.TEXT,
          Text: payout?.reference,
        },
        {
          key: 'status',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights: [
            ...(!!payout?.errorMessage
              ? [
                  <Highlight type="error" key={`error-${payout?.reference}-error`}>
                    {t('massPayouts.errors', {
                      numberOfErrors: formatErrorMsg(payout?.errorMessage?.value).errorList?.length,
                    })}
                  </Highlight>,
                ]
              : [
                  <Highlight
                    type="success"
                    variant="emphasis"
                    iconElement={<Icon name="Check" />}
                    key={`success-${payout?.reference}-success`}
                  >
                    {t('massPayouts.fixed')}
                  </Highlight>,
                ]),
          ],
        },
        {
          key: 'actions',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            ...(payout?.status === RecordStatus.DRAFT || payout?.status === RecordStatus.ERROR
              ? [
                  <Button
                    key={`edit-${payout?.reference}-edit-button`}
                    iconElement={<Icon name="SimplePencilCustom" />}
                    variant="ghost"
                    onClick={e => handleShowEditCryptoPayoutModal(e, payout)}
                  />,
                ]
              : []),
            <Button
              key={`delete-${payout?.reference}-delete-button`}
              iconElement={<Icon name="Trash" />}
              variant="ghost"
              onClick={e => handleDeleteCryptoPayout(payout)}
            />,
          ],
        },
      ],
    }
  })
}
